import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SharedMemoryParticipant {
  child_id: string | null;
  space_member_id: string | null;
  display_name: string | null;
  child: { id: string; name: string; avatar_color: string | null } | null;
  member: { id: string; display_name: string | null } | null;
}

export interface SharedMemoryData {
  id: string;
  title: string | null;
  ai_chapter: string | null;
  categories: string[];
  audio_url: string | null;
  image_url: string | null;
  created_at: string;
  memory_participants: SharedMemoryParticipant[];
}

export interface SharedMemoryPageData {
  memory: SharedMemoryData;
  expiresAt: string;
}

/**
 * Fetches a shared memory by public token (unauthenticated).
 * Returns null if the token is not found or the join returned no memory row.
 */
export async function fetchSharedMemoryByToken(
  token: string,
): Promise<SharedMemoryPageData | null> {
  const { data, error } = await supabase
    .from("shared_memories")
    .select(
      `expires_at,
       memories!memory_id(
         id, title, ai_chapter, categories,
         audio_url, image_url, created_at,
         memory_participants(
           child_id, space_member_id, display_name,
           child:children(id, name, avatar_color),
           member:space_members(id, display_name)
         )
       )`,
    )
    .eq("token", token)
    .single();
  if (error || !data) return null;
  const row = data as unknown as { expires_at: string; memories: SharedMemoryData };
  if (!row.memories) return null;
  return { memory: row.memories, expiresAt: row.expires_at };
}

export interface ShareInfo {
  token: string;
  viewCount: number;
  expiresAt: string;
}

/** Fetches token, view_count, and expires_at for the active share of a memory. */
export async function fetchShareDetails(
  memoryId: string,
  userId: string,
): Promise<ShareInfo | null> {
  const { data } = await supabase
    .from("shared_memories")
    .select("token, view_count, expires_at")
    .eq("memory_id", memoryId)
    .eq("created_by", userId)
    .gt("expires_at", new Date().toISOString())
    .is("revoked_at", null)
    .maybeSingle();
  if (!data) return null;
  const row = data as { token: string; view_count: number; expires_at: string };
  return { token: row.token, viewCount: row.view_count, expiresAt: row.expires_at };
}

/** One-shot check used inside action handlers (handleShare). */
export async function fetchActiveShare(
  memoryId: string,
  userId: string,
): Promise<{ token: string } | null> {
  const { data } = await supabase
    .from("shared_memories")
    .select("token")
    .eq("memory_id", memoryId)
    .eq("created_by", userId)
    .gt("expires_at", new Date().toISOString())
    .is("revoked_at", null)
    .maybeSingle();
  return data as { token: string } | null;
}

export function useMemoryShareInfo(
  memoryId: string,
  userId: string | undefined,
  isOpen: boolean,
) {
  const query = useQuery({
    queryKey: ["memory-share-info", memoryId, userId],
    enabled: isOpen && !!memoryId && !!userId,
    staleTime: 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("shared_memories")
        .select("token, view_count, expires_at")
        .eq("memory_id", memoryId)
        .eq("created_by", userId!)
        .gt("expires_at", new Date().toISOString())
        .is("revoked_at", null)
        .maybeSingle();
      if (!data) return null;
      const row = data as { token: string; view_count: number; expires_at: string };
      return {
        token: row.token,
        viewCount: row.view_count,
        expiresAt: row.expires_at,
      } as ShareInfo;
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
